import { useGetWelcomeCallStipRulesHistoryQuery } from '@/services/welcomeCallStipRules';
import { DateTime } from 'luxon';

import { ProgramType } from 'types';

const FIELD_LABELS: Record<string, string> = {
  'age.enabled': 'Age Enabled',
  'age.value': 'Age',
  'age.applicationRate': 'Age Apply Rate',
  'state.enabled': 'State Enabled',
  'state.value': 'State',
  'state.applicationRate': 'State Apply Rate',
  'salesRepStatus.enabled': 'Sales Rep Status Enabled',
  'salesRepStatus.value': 'Sales Rep Status',
  'salesRepStatus.applicationRate': 'Sales Rep Status Apply Rate',
  'quoteMonthlyPayment.enabled': 'Quote Monthly Payment Enabled',
  'quoteMonthlyPayment.value': 'Quote Monthly Payment',
  'quoteMonthlyPayment.applicationRate': 'Quote Monthly Payment Apply Rate',
  'preferredLanguage.enabled': 'Preferred Language Enabled',
  'preferredLanguage.value': 'Preferred Language',
  'preferredLanguage.applicationRate': 'Preferred Language Apply Rate',
};

// The API passes the raw captureDatabaseEvent path through untransformed ('value/age/applicationRate'),
// so handle both slash- and dot-delimited forms defensively.
export const labelForDiffPath = (path: string): string => {
  const key = path.replace(/^value[./]/, '').replace(/\//g, '.');
  const templateOverride = key.match(/^state\.templateIds\.(.+)$/);
  if (templateOverride) return `State Template ID (${templateOverride[1]})`;
  return FIELD_LABELS[key] ?? key;
};

export const formatDiffValue = (value: unknown): string => {
  if (value === undefined || value === null) return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  return String(value);
};

const isNoOpDiff = (from: unknown, to: unknown) => JSON.stringify(from) === JSON.stringify(to);

export const ChangeHistory = ({ programType = ProgramType.solar }: { programType?: ProgramType }) => {
  const { data, isLoading, isError } = useGetWelcomeCallStipRulesHistoryQuery({
    programType,
  });

  const entries = (data?.data ?? [])
    .map((entry) => ({
      ...entry,
      diffs: entry.diffs.filter((d) => !isNoOpDiff(d.from, d.to)),
    }))
    .filter((entry) => entry.diffs.length > 0);

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-bold">Change History</h3>
      {isLoading ? (
        <p className="text-sm text-text-secondary">Loading...</p>
      ) : entries.length === 0 && isError ? (
        <p className="text-sm text-text-error">History unavailable.</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-text-secondary">No changes recorded yet.</p>
      ) : (
        <>
          {entries.map((entry) => (
            <div
              key={`${entry.timestamp}-${entry.actorEmail ?? 'system'}`}
              data-testid="history-entry"
              className="flex flex-col gap-2 rounded-[6px] bg-canvas-highlight-default p-16"
            >
              <div className="text-xs text-text-tertiary">
                {DateTime.fromISO(entry.timestamp).toFormat('MMM d, yyyy, h:mm a')} by {entry.actorEmail ?? 'system'}
              </div>
              <ul className="list-disc pl-16 text-sm">
                {entry.diffs.map((diff) => (
                  <li key={diff.path}>
                    {labelForDiffPath(diff.path)}: {formatDiffValue(diff.from)} {'→'} {formatDiffValue(diff.to)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {/* Both the condition and the note count use the server page size (data.data.length)
              so the "N of M" units match -- entries.length can only differ if the client's
              no-op filter disagrees with the server's, which the mirrored filters prevent. */}
          {!!data && data.total > data.data.length && (
            <p className="text-xs text-text-tertiary">
              Showing latest {data.data.length} of {data.total} changes
            </p>
          )}
        </>
      )}
    </div>
  );
};
